import { useState } from "react";
import AdminProductService from "../services/admin/AdminProductService";
import { useNotification } from "../context/NotificationContext";
import { useAdminPermissions } from "./useAdminPermissions";

export function useProductsData() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingStatusMap, setLoadingStatusMap] = useState({});
  const [loadingPopularMap, setLoadingPopularMap] = useState({});
  const { showNotification } = useNotification();
  const { checkPermission } = useAdminPermissions();

  const fetchProducts = async (params = {}) => {
    setLoading(true);
    try {
      const res = await AdminProductService.list(params);
      setProducts(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  const addProducts = (newProducts) => {
    setProducts((prev) => [...newProducts, ...prev]);
  };

  const handleDeleteProduct = async (id) => {
    if (!checkPermission("admin", "delete products")) return;
    try {
      await AdminProductService.delete(id);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      showNotification({
        type: "success",
        title: "Product Deleted",
        message: "Product has been deleted successfully.",
      });
    } catch (err) {
      showNotification({
        type: "error",
        title: "Delete Failed",
        message: err?.response?.data?.message || "Failed to delete product",
      });
    }
  };

  const handleToggleStatus = async (id, status) => {
    if (!checkPermission("admin", "toggle product status")) return;
    try {
      setLoadingStatusMap((prev) => ({ ...prev, [id]: true }));
      const updated = await AdminProductService.toggleStatus(id, status);
      setProducts((prev) =>
        prev.map((p) => (p.id === id ? { ...p, status: updated.status } : p))
      );
      showNotification({
        type: "success",
        title: "Status Updated",
        message: `Product status is now ${updated.status}.`,
      });
    } catch (err) {
      showNotification({
        type: "error",
        title: "Toggle Failed",
        message: err?.response?.data?.message || "Failed to toggle status",
      });
    } finally {
      setLoadingStatusMap((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleTogglePopular = async (id, isPopular) => {
    if (!checkPermission("admin", "update popularity status")) return;
    try {
      setLoadingPopularMap((prev) => ({ ...prev, [id]: true }));
      const result = await AdminProductService.togglePopular(id, isPopular);

      setProducts((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, popular: result.is_popular }
            : p
        )
      );
      showNotification({
        type: "success",
        title: "Popularity Updated",
        message: `Product is now ${result.is_popular ? "featured" : "removed from featured"}.`,
      });
    } catch (err) {
      showNotification({
        type: "error",
        title: "Toggle Failed",
        message: err?.response?.data?.message || "Failed to toggle popularity",
      });
      console.error(err);
    } finally {
      setLoadingPopularMap((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleBulkAction = async (action, ids) => {
    if (!checkPermission("admin", "perform bulk actions")) return;
    if (action === "delete") {
      showNotification({
        type: "info",
        title: "Processing",
        message: "Deleting selected products...",
      });
      try {
        await AdminProductService.bulkDelete(ids);
        setProducts((prev) => prev.filter((p) => !ids.includes(p.id)));
        showNotification({
          type: "success",
          title: "Bulk Delete Complete",
          message: `Successfully deleted ${ids.length} products.`,
        });
      } catch (err) {
        showNotification({
          type: "error",
          title: "Bulk Delete Failed",
          message: err?.response?.data?.message || "Failed to delete products",
        });
      }
    }
  };

  return {
    products,
    loading,
    fetchProducts,
    addProducts,
    handleDeleteProduct,
    handleToggleStatus,
    handleTogglePopular,
    handleBulkAction,
    setProducts
  };
}
